import {
  AttachmentToReference as SigningStateAttachment,
  AutofillFieldSupportFieldType,
  CheckboxFieldToReference,
  FieldNodeToReference,
  FieldNodeToReference as SigningStateFieldNode,
  ParticipantToReference as SigningStateParticipant,
  TextBaseConfig,
  ValueType,
  VerificationMethod,
  VerificationToReference,
} from '@modusign/react-signing/types';
import { v4 } from 'uuid';

import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import {
  Field as TransportLayerField,
  TextFieldBaseConfig as TransportLayerTextFieldBaseConfig,
} from '@app/store/transport-layer/types/field';
import { FieldGroup as TransportLayerFieldGroup } from '@app/store/transport-layer/types/fieldGroup';
import {
  Participant as TransportLayerParticipant,
  ParticipantType,
  SignerWithSigningProperties as TransportLayerSignerWithSigningProperties,
} from '@app/store/transport-layer/types/participant';
import { WithId, WithOptionalId } from '@app/store/transport-layer/types/utility';
import {
  Verification as TransportLayerVerification,
  VerificationListItem as TransportVerificationListItem,
  VerificationMethod as TransportVerificationMethod,
  VerificationMethodForListItem as TransportVerificationMethodForListItem,
  VerificationWithFailureProps as TransportLayerVerificationWithFailureProps,
  WithFailureProps,
} from '@app/store/transport-layer/types/verification';

type MappingFrom =
  | {
      type: 'SIGNING';
      signatureUrl?: string;
    }
  | { type: 'SETUP' };

// TODO(@DevSDK: TS-essential - Buildable 활용하기
type TransportLayerAttachmentSendReceiveUnion =
  | TransportLayerAttachment['ToSend']
  | TransportLayerAttachment['ToReceive']
  | WithId<TransportLayerAttachment['ToReceive']>;

type TransportLayerFieldSendReceiveUnion =
  | TransportLayerField['ToSend']
  | TransportLayerField['ToReceive']
  | WithId<TransportLayerField['ToReceive']>;

type TransPortLayerVerificationUnion =
  | TransportLayerVerification
  | TransportVerificationListItem
  | TransportLayerVerificationWithFailureProps;

export function toSigningStateForSetup(
  attachments: TransportLayerAttachmentSendReceiveUnion[],
  participants: TransportLayerParticipant[],
  fields: TransportLayerFieldSendReceiveUnion[],
  fieldGroups: TransportLayerFieldGroup[],
  verifications: TransPortLayerVerificationUnion[]
): {
  attachments: SigningStateAttachment[];
  participants: SigningStateParticipant[];
  fields: SigningStateFieldNode[];
  verifications: VerificationToReference[];
} {
  return {
    attachments: convertAttachmentsToSigningState(attachments),
    participants: convertParticipantToSigningState(participants),
    fields: transformTransformLayerFieldToSigningState(fields, fieldGroups, participants, {
      type: 'SETUP',
    }),
    verifications: verifications.map<VerificationToReference>((verification) => ({
      participantId: verification.participant.id ?? verification.participant.dataLabel,
      methods: convertVerificationMethods(verification.methods),
    })),
  };
}

export function toSigningStateForSigning(
  attachments: TransportLayerAttachmentSendReceiveUnion[],
  participants: TransportLayerParticipant[],
  fields: TransportLayerFieldSendReceiveUnion[],
  fieldGroups: TransportLayerFieldGroup[],
  verifications: TransPortLayerVerificationUnion[],
  signatureUrl?: string
): {
  attachments: SigningStateAttachment[];
  participants: SigningStateParticipant[];
  fields: SigningStateFieldNode[];
  verifications: VerificationToReference[];
} {
  return {
    attachments: convertAttachmentsToSigningState(attachments),
    participants: convertParticipantToSigningState(participants),
    fields: transformTransformLayerFieldToSigningState(fields, fieldGroups, participants, {
      type: 'SIGNING',
      signatureUrl,
    }),
    verifications: verifications.map<VerificationToReference>((verification) => ({
      participantId: verification.participant.id ?? verification.participant.dataLabel,
      methods: convertVerificationMethods(verification.methods),
    })),
  };
}

/**
 * (@Juyeong-Byeon): verification을 api response로 받을 때 3가지 타입이 있다.
 * @see https://modusign.slack.com/archives/C04902B1JF7/p1685500731131439
 */
type Method =
  | TransportVerificationMethodForListItem
  | TransportVerificationMethod
  | WithFailureProps<TransportVerificationMethod>;

function assignVerificationMethodOptionalProps(method: Method, target: VerificationMethod) {
  if ('failureCount' in method) {
    target.lastFailedAt = method.lastFailedAt;
    target.failureCount = method.failureCount;
  }

  if ('hint' in method) {
    target.hint = method.hint;
  }
}

function convertVerificationMethods(methods: Method[]): VerificationMethod[] {
  return methods
    .map<VerificationMethod | null>((method) => {
      const parsedInfo = method.passValue?.split('|') ?? [];
      let parsed: VerificationMethod;

      if (method.type === 'D_CERT') {
        parsed = {
          type: 'D_CERT',
          passValue: method.passValue ?? '',
          displayName: parsedInfo[0] ?? null,
          displayBizNumber: parsedInfo[1] ?? null,

          // optional props
          failureCount: undefined,
          lastFailedAt: undefined,
          hint: null,
        };

        assignVerificationMethodOptionalProps(method, parsed);

        return parsed;
      }

      if (method.type === 'MOBILE_IDENTIFICATION') {
        parsed = {
          type: 'MOBILE_IDENTIFICATION',
          passValue: method.passValue ?? '',
          displayName: parsedInfo[0] ?? null,
          displayPhoneNumber: parsedInfo[1] ?? null,

          // optional props
          failureCount: undefined,
          lastFailedAt: undefined,
          hint: null,
        };

        assignVerificationMethodOptionalProps(method, parsed);

        return parsed;
      }

      if (method.type === 'PASSWORD') {
        parsed = {
          type: 'PASSWORD',
          passValue: method.passValue ?? '',

          // optional props
          failureCount: undefined,
          lastFailedAt: undefined,
          hint: null,
        };

        assignVerificationMethodOptionalProps(method, parsed);
        return parsed;
      }

      return null;
    })
    .filter((method): method is VerificationMethod => method != null);
}

function convertAttachmentInputToFile(
  input: TransportLayerAttachmentSendReceiveUnion['input']
): SigningStateAttachment['file'] {
  const file = (input as TransportLayerAttachment['ToReceive']['input'])?.file;
  if (file) {
    return {
      url: file.url ?? '',
      extension: file.extension,
      name: file.name,
      size: file.size,
    };
  }
  return undefined;
}

function convertAttachmentInput(
  input: TransportLayerAttachmentSendReceiveUnion['input']
): SigningStateAttachment['input'] {
  const maybeInputToSend = (input as TransportLayerAttachment['ToSend']['input'])?.file;
  const maybeInputToReceive = (input as TransportLayerAttachment['ToReceive']['input'])?.file;

  // TODO: 아래 로직들 별도 매핑 함수로 분리하기 FYI - https://github.com/modusign/modusign-client/pull/9707#discussion_r1314473863
  // From Copy
  if (!!maybeInputToReceive?.name && !!maybeInputToSend?.fileId) {
    return {
      fileId: maybeInputToSend.fileId,
      fileName: maybeInputToReceive.name,
      token: maybeInputToSend.token,
    };
  }

  if (maybeInputToSend?.fileId) {
    return {
      fileId: maybeInputToSend.fileId,
      fileName: maybeInputToSend.fileName,
      token: maybeInputToSend.token,
    };
  }
  return undefined;
}

export function convertAttachmentsToSigningState(
  attachments: WithOptionalId<TransportLayerAttachmentSendReceiveUnion>[]
) {
  return attachments.map<SigningStateAttachment>((src) => ({
    id: getId(src),
    title: src.title,
    config: {
      required: src.config.required,
    },
    message: src.message,
    documentId: src.documentId,
    participantId: src.participant.id || src.participant.dataLabel,
    dataLabel: src.dataLabel,
    file: convertAttachmentInputToFile(src.input),
    input: convertAttachmentInput(src.input),
  }));
}

function convertParticipantToSigningState(
  participants: WithOptionalId<TransportLayerParticipant>[]
): SigningStateParticipant[] {
  let signerCounter = 0;
  return participants.map<SigningStateParticipant>((src) => {
    const signingProperties =
      'locale' in src ? (src as WithId<TransportLayerSignerWithSigningProperties>) : undefined;

    if (src.type === 'HOST') {
      return {
        type: 'HOST',
        dataLabel: src.dataLabel,
        id: src.id ?? src.dataLabel,
        contact: src.contact,
        name: src.name,
        locale: signingProperties?.locale ?? 'ko',
      };
    }

    if (src.type === 'REQUESTER') {
      return {
        type: 'REQUESTER',
        dataLabel: src.dataLabel,
        id: src.id ?? src.dataLabel,
        contact: src.contact,
        name: src.name,
      };
    }
    signerCounter += 1;

    return {
      type: 'SIGNER',
      id: src.id ?? src.dataLabel,
      dataLabel: src.dataLabel,
      order: src.order,
      index: signerCounter - 1,
      signingMethod: src.signingMethod,
      contact: src.contact,
      name: src.name,
      role: src.role,
      signingDuration: signingProperties?.signingDuration || undefined,
      signingPeriod: signingProperties?.signingPeriod || undefined,
      locale: signingProperties?.locale,
      message: signingProperties?.message || undefined,
      host: signingProperties?.host
        ? {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            participantId: signingProperties.host.id!,
          }
        : undefined,
    };
  });
}

type ParticipantByDataLabel = Record<string, TransportLayerParticipant>;

function getFieldParticipantType(
  field: TransportLayerFieldSendReceiveUnion,
  participantByDataLabel: ParticipantByDataLabel
): ParticipantType {
  return participantByDataLabel[field.participant.dataLabel].type;
}

function shouldInputBeNull(
  field: TransportLayerFieldSendReceiveUnion,
  participantByDataLabel: ParticipantByDataLabel,
  from: MappingFrom
) {
  return (
    from.type === 'SETUP' && getFieldParticipantType(field, participantByDataLabel) === 'SIGNER'
  );
}

function transformTransformLayerFieldToSigningState(
  fields: TransportLayerFieldSendReceiveUnion[],
  fieldGroups: TransportLayerFieldGroup[],
  participants: TransportLayerParticipant[],
  from: MappingFrom
): SigningStateFieldNode[] {
  const participantByDataLabel: ParticipantByDataLabel = {};
  participants.forEach((p) => {
    participantByDataLabel[p.dataLabel] = p;
  });

  const groupMap: Record<string, WithOptionalId<TransportLayerFieldSendReceiveUnion>[]> = {};
  const result: SigningStateFieldNode[] = [];
  // mark group by group id and some business rule like checkbox group
  fields.forEach((field) => {
    // 만약 그룹 라벨이 있는 경우
    if (field.groupDataLabel) {
      if (!groupMap[field.groupDataLabel]) {
        groupMap[field.groupDataLabel] = [];
      }
      groupMap[field.groupDataLabel].push(field);
    }
    // 없는데 서명자 체크박스 필드일 경우
    else if (
      field.type === 'CHECKBOX' &&
      getFieldParticipantType(field, participantByDataLabel) === 'SIGNER'
    ) {
      fieldGroups.push({
        config: {
          maximumAllowed: 1,
          minimumRequired: field.config.required ? 1 : 0,
        },
        dataLabel: field.dataLabel,
        participant: field.participant,
        type: 'CHECKBOX_GROUP',
      });
      groupMap[field.dataLabel] = [{ ...field, dataLabel: '1' }];
    }
    // 단일 필드 일 경우
    else {
      const signingStateField = transformField(field, participantByDataLabel, from);
      if (signingStateField) {
        result.push(signingStateField);
      }
    }
  });

  fieldGroups.forEach((fieldGroup) => {
    if (fieldGroup.type === 'CHECKBOX_GROUP') {
      const children = groupMap[fieldGroup.dataLabel].map<CheckboxFieldToReference>((field) => {
        return {
          id: field.id || v4(),
          type: 'CHECKBOX',
          config: {
            // TODO(@DevSDK): register issue unnecesarry config?
            required: false,
            valueType: 'BOOLEAN',
          },
          dataLabel: field.dataLabel,
          index: field.groupDataLabel ? Number(field.dataLabel) : 1,
          input: shouldInputBeNull(field, participantByDataLabel, from)
            ? null
            : {
                overridingConfig: {
                  required: false,
                  valueType: 'BOOLEAN' as const,
                },
                value: field.input?.value === 'TRUE',
                // 아래 이슈로 인해 destruction을 사용합니다.
                // https://github.com/modusign/react-signing/issues/110
                position: { ...field.guide.position },
                size: { ...field.guide.size },
              },
          parent: null,
          participantId: field.participant.id ?? field.participant.dataLabel,
          // 아래 이슈로 인해 destruction을 사용합니다.
          // https://github.com/modusign/react-signing/issues/110
          position: { ...field.guide.position },
          size: { ...field.guide.size },
        } as const;
      });

      result.push({
        type: 'CHECKBOX_GROUP',
        id: v4(),
        config: {
          mode: fieldGroup.config.maximumAllowed === 1 ? 'RADIO' : 'CHECKBOX',
          required: fieldGroup.config.minimumRequired > 0,
        },
        dataLabel: fieldGroup.dataLabel,
        participantId: fieldGroup.participant.id ?? fieldGroup.participant.dataLabel,
        children,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        size: { ...children[0].size },
        pages: Array.from(new Set(children.map((child) => child.position.page))),
      } as const);
    } else if (fieldGroup.type === 'AUTOFILL_GROUP') {
      type AutofillField = Extract<FieldNodeToReference, { type: AutofillFieldSupportFieldType }>;
      const children = groupMap[fieldGroup.dataLabel]
        .map((field): AutofillField | null => {
          switch (field.type) {
            case 'NAME':
              return {
                id: field.id || v4(),
                parent: null,
                type: 'NAME',
                config: {
                  ...transformTextBaseConfig(field.config),
                },
                dataLabel: field.dataLabel,
                input: shouldInputBeNull(field, participantByDataLabel, from)
                  ? null
                  : {
                      overridingConfig: field.input?.overrideConfig
                        ? {
                            ...transformTextBaseConfig(field.input.overrideConfig),
                          }
                        : {},
                      value:
                        field.input?.value ??
                        participantByDataLabel[field.participant.dataLabel]?.name ??
                        '',
                      // 아래 이슈로 인해 destruction을 사용합니다.
                      // https://github.com/modusign/react-signing/issues/110
                      position: { ...field.guide.position },
                      size: { ...field.guide.size },
                    },
                participantId: field.participant.id || field.participant.dataLabel,
                // 아래 이슈로 인해 destruction을 사용합니다.
                // https://github.com/modusign/react-signing/issues/110
                position: { ...field.guide.position },
                size: { ...field.guide.size },
              } as const;
            case 'COMPANY_NAME':
              return {
                id: field.id || v4(),
                parent: null,
                type: 'COMPANY_NAME',
                config: {
                  ...transformTextBaseConfig(field.config),
                },
                dataLabel: field.dataLabel,
                input: shouldInputBeNull(field, participantByDataLabel, from)
                  ? null
                  : {
                      overridingConfig: field.input?.overrideConfig
                        ? {
                            ...transformTextBaseConfig(field.input.overrideConfig),
                          }
                        : {},
                      value: field.input?.value ?? '',
                      // 아래 이슈로 인해 destruction을 사용합니다.
                      // https://github.com/modusign/react-signing/issues/110
                      position: { ...field.guide.position },
                      size: { ...field.guide.size },
                    },
                participantId: field.participant.id || field.participant.dataLabel,
                // 아래 이슈로 인해 destruction을 사용합니다.
                // https://github.com/modusign/react-signing/issues/110
                position: { ...field.guide.position },
                size: { ...field.guide.size },
              } as const;
            case 'ADDRESS':
              return {
                id: field.id || v4(),
                parent: null,
                type: 'ADDRESS',
                config: {
                  ...transformTextBaseConfig(field.config),
                },
                dataLabel: field.dataLabel,
                input: shouldInputBeNull(field, participantByDataLabel, from)
                  ? null
                  : {
                      overridingConfig: field.input?.overrideConfig
                        ? {
                            ...transformTextBaseConfig(field.input.overrideConfig),
                          }
                        : {},
                      value: field.input?.value ?? {
                        address1: '',
                        address2: null,
                        city: null,
                        country: 'KR',
                        province: '',
                        zip: '',
                      },
                      // 아래 이슈로 인해 destruction을 사용합니다.
                      // https://github.com/modusign/react-signing/issues/110
                      position: { ...field.guide.position },
                      size: { ...field.guide.size },
                    },
                participantId: field.participant.id || field.participant.dataLabel,
                // 아래 이슈로 인해 destruction을 사용합니다.
                // https://github.com/modusign/react-signing/issues/110
                position: { ...field.guide.position },
                size: { ...field.guide.size },
              } as const;
            default:
              return null;
          }
        })
        .filter((field): field is AutofillField => field !== null);
      result.push({
        type: 'AUTOFILL_GROUP',
        id: v4(),
        config: {},
        dataLabel: fieldGroup.dataLabel,
        participantId: fieldGroup.participant.id ?? fieldGroup.participant.dataLabel,
        children,
        pages: Array.from(new Set(children.map((child) => child.position.page))),
      } as const);
    }
  });
  return result;
}

// TOOD(@DevSDK): 공통되는 코드 묶을 수 있으면 묶기
function transformField(
  field: WithOptionalId<TransportLayerFieldSendReceiveUnion>,
  participantByDataLabel: ParticipantByDataLabel,
  from: MappingFrom
): SigningStateFieldNode | null {
  switch (field.type) {
    case 'SIGNATURE':
      return {
        id: field.id || v4(),
        type: 'SIGNATURE',
        config: {
          allow: field.config.allow,
          disableSignatureGenerator: field.config.disableSignatureGenerator,
          required: field.config.required,
          valueType: 'SIGNATURE',
        },
        url:
          from.type === 'SIGNING' && from.signatureUrl && field.input?.value
            ? from?.signatureUrl
            : undefined,
        dataLabel: field.dataLabel,
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              overridingConfig: field.input?.overrideConfig
                ? {
                    allow: field.input.overrideConfig.allow,
                    required: field.input.overrideConfig.required,
                    valueType: 'SIGNATURE' as const,
                  }
                : {},
              value:
                from.type === 'SIGNING' && from.signatureUrl && field.input?.value
                  ? field.input?.value
                  : '',
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position, ...field.input?.position },
              size: { ...field.guide.size, ...field.input?.size },
            },
        participantId: field.participant.id ?? field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;
    case 'TEXT':
      return {
        id: field.id || v4(),
        type: 'TEXT',
        config: {
          ...transformTextBaseConfig(field.config),
          format: field.config.format,
          valueDescription: field.config.valueDescription,
        },
        dataLabel: field.dataLabel,
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              overridingConfig: field.input?.overrideConfig
                ? {
                    ...transformTextBaseConfig(field.input.overrideConfig),
                    format: field.input.overrideConfig.format,
                  }
                : {},
              value: field.input?.value ?? '',
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position },
              size: { ...field.guide.size },
            },
        participantId: field.participant.id || field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;
    case 'DROPDOWN':
      return {
        id: field.id || v4(),
        type: 'DROPDOWN',
        config: {
          ...transformTextBaseConfig(field.config),
          // 개행문자는 select 표준에 의해 제거됩니다.
          // FYI: https://github.com/modusign/modusign-client/pull/9981
          options: field.config.options.map(({ value }) => ({
            value: value.replace(/[\s]+/g, ' '),
          })),
        },
        dataLabel: field.dataLabel,
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              overridingConfig: field.input?.overrideConfig
                ? {
                    ...transformTextBaseConfig(field.input.overrideConfig),
                    // 개행문자는 select 표준에 의해 제거됩니다.
                    // FYI: https://github.com/modusign/modusign-client/pull/9981
                    options: field.input.overrideConfig.options.map(({ value }) => ({
                      value: value.replace(/[\s]+/g, ' '),
                    })),
                  }
                : {},
              value:
                from.type === 'SIGNING' && !field.input?.value && field.config.defaultInputValue
                  ? field.config.defaultInputValue
                  : field.input?.value ?? '',
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position },
              size: { ...field.guide.size },
            },
        participantId: field.participant.id || field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;

    case 'IMAGE':
      return {
        type: 'IMAGE',
        config: {
          required: field.config.required,
          valueType: 'IMAGE',
        },
        dataLabel: field.dataLabel,
        id: field.id || v4(),
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              overridingConfig: field.input?.overrideConfig
                ? {
                    required: field.input.overrideConfig.required,
                    valueType: 'IMAGE' as const,
                  }
                : {},
              value:
                field.input && field.input.value && typeof field.input.value !== 'string'
                  ? {
                      fileId: field.input?.value?.fileId ?? '',
                      otp: field.input?.value?.otp ?? '',
                    }
                  : {
                      // TODO(@DevSDK): ImageInput: maybe we need to declare this to optional?
                      fileId: '',
                      otp: '',
                    },
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position },
              size: { ...field.guide.size },
            },
        participantId: field.participant.id ?? field.participant.dataLabel,
        url:
          field.input && field.input.value && typeof field.input?.value === 'string'
            ? field.input.value
            : undefined,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;
    case 'CHECKBOX':
      return {
        id: field.id || v4(),
        type: 'CHECKBOX',
        config: {
          required: false,
          valueType: 'BOOLEAN',
        },
        dataLabel: field.dataLabel,
        index: 0,
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              value: field.input?.value === 'TRUE',
              overridingConfig: field.input?.overrideConfig
                ? {
                    required: field.input.overrideConfig.required,
                    valueType: 'BOOLEAN' as const,
                  }
                : {},
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position },
              size: { ...field.guide.size },
            },
        participantId: field.participant.id ?? field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
        parent: null,
      };
    case 'DATE':
      return {
        id: field.id || v4(),
        type: 'DATE',
        config: {
          ...transformTextBaseConfig(field.config),
          displayFormat: field.config.displayFormat,
        },
        dataLabel: field.dataLabel,
        input: shouldInputBeNull(field, participantByDataLabel, from)
          ? null
          : {
              overridingConfig: field.input?.overrideConfig
                ? {
                    ...transformTextBaseConfig(field.input.overrideConfig),
                    displayFormat: field.config.displayFormat,
                  }
                : {},
              value: field.input?.value ?? '',
              // 아래 이슈로 인해 destruction을 사용합니다.
              // https://github.com/modusign/react-signing/issues/110
              position: { ...field.guide.position },
              size: { ...field.guide.size },
            },
        participantId: field.participant.id || field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;
    case 'SIGNING_DATE':
      return {
        id: field.id || v4(),
        type: 'SIGNING_DATE',
        config: {
          ...transformTextBaseConfig(field.config),
          displayFormat: field.config.displayFormat,
        },
        dataLabel: field.dataLabel,
        input: null,
        participantId: field.participant.id || field.participant.dataLabel,
        // 아래 이슈로 인해 destruction을 사용합니다.
        // https://github.com/modusign/react-signing/issues/110
        position: { ...field.guide.position },
        size: { ...field.guide.size },
      } as const;
    default:
      return null;
  }
}

function transformTextBaseConfig<T extends ValueType, TInputValue = string>(
  config: TransportLayerTextFieldBaseConfig<T, TInputValue>
): TextBaseConfig<T, TInputValue> {
  return {
    defaultInputValue: config.defaultInputValue,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    valueType: config.valueType,
  };
}

function getId<R extends { id?: string } | object>(resource: R): string {
  return 'id' in resource && resource.id ? resource.id : v4();
}
